import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Download, Calendar as CalendarIcon, Trash2, Search, GripVertical, Type, Check, ChevronDown, ChevronRight, FileCheck, Clock, AlertCircle, ArrowUp, ArrowDown, ArrowUpDown, Filter, X } from "lucide-react";
import axios from "axios";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

// Format date to short format YY-MM-DD
const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const yy = String(date.getFullYear()).slice(-2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

// Resizable Column Header Component
const ResizableColumnHeader = ({ 
  children, 
  columnKey,
  width, 
  onResize, 
  minWidth = 50,
  className = "",
  align = "left"
}) => {
  const headerRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(minWidth, startWidth.current + diff);
      onResize(columnKey, newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, columnKey, minWidth, onResize]);

  return (
    <th 
      ref={headerRef}
      className={cn(
        "relative px-3 py-2 text-xs font-semibold text-slate-600 uppercase tracking-wide border-r border-slate-200 select-none",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className
      )}
      style={{ width: `${width}px`, minWidth: `${minWidth}px` }}
    >
      {children}
      {/* Resize Handle - visible border */}
      <div
        className={cn(
          "absolute top-0 right-0 w-1 h-full cursor-col-resize transition-colors",
          "bg-slate-300 hover:bg-indigo-500",
          isResizing && "bg-indigo-500"
        )}
        onMouseDown={handleMouseDown}
        style={{ touchAction: 'none' }}
      >
        <div className="absolute top-0 right-0 w-4 h-full -mr-2 cursor-col-resize" />
      </div>
    </th>
  );
};

// Excel-style Column Header with Sort & Filter
const ExcelHeader = ({ 
  column, 
  label, 
  width, 
  minWidth = 50,
  onResize, 
  sortConfig, 
  onSort,
  filterValue,
  onFilter,
  align = "left",
  filterable = true 
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [localFilter, setLocalFilter] = useState(filterValue || "");
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const diff = e.clientX - startX.current;
      const newWidth = Math.max(minWidth, startWidth.current + diff);
      onResize(column, newWidth);
    };

    const handleMouseUp = () => setIsResizing(false);

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, minWidth, onResize, column]);

  const isSorted = sortConfig?.column === column;
  const isFiltered = filterValue && filterValue.length > 0;

  const SortIcon = isSorted 
    ? (sortConfig.direction === 'asc' ? ArrowUp : ArrowDown)
    : ArrowUpDown;

  return (
    <th 
      className={cn(
        "relative group bg-slate-100 border-b border-r border-slate-200 select-none whitespace-nowrap",
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"
      )}
    >
      <div className={cn(
        "flex items-center gap-1 px-2 py-1.5 h-8",
        align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start"
      )}>
        {/* Sort button */}
        <button
          onClick={() => onSort(column)}
          className={cn(
            "flex items-center gap-1 text-xs font-semibold uppercase tracking-wide hover:text-indigo-600 transition-colors",
            isSorted ? "text-indigo-600" : "text-slate-600"
          )}
        >
          <SortIcon size={11} className={isSorted ? "text-indigo-500" : "text-slate-400"} />
          <span>{label}</span>
        </button>

        {/* Filter dropdown */}
        {filterable && (
          <DropdownMenu open={showFilter} onOpenChange={setShowFilter}>
            <DropdownMenuTrigger asChild>
              <button 
                className={cn(
                  "p-0.5 rounded hover:bg-slate-200 transition-colors",
                  isFiltered ? "text-indigo-600" : "text-slate-400 opacity-0 group-hover:opacity-100"
                )}
              >
                <Filter size={11} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48 p-2">
              <div className="space-y-2">
                <Input
                  placeholder={`Filtrera ${label.toLowerCase()}...`}
                  value={localFilter}
                  onChange={(e) => setLocalFilter(e.target.value)}
                  className="h-7 text-xs"
                  autoFocus
                />
                <div className="flex gap-1">
                  <Button 
                    size="sm" 
                    className="h-6 text-xs flex-1"
                    onClick={() => { onFilter(column, localFilter); setShowFilter(false); }}
                  >
                    Tillämpa
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="h-6 text-xs"
                    onClick={() => { setLocalFilter(""); onFilter(column, ""); setShowFilter(false); }}
                  >
                    <X size={12} />
                  </Button>
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Resize handle */}
      <div
        className={cn(
          "absolute right-0 top-0 h-full w-1 cursor-col-resize transition-colors",
          "hover:bg-indigo-500 bg-slate-300",
          isResizing && "bg-indigo-500"
        )}
        onMouseDown={handleMouseDown}
      />
    </th>
  );
};

// Sortable Row Component for form
const SortableRow = ({ id, children, isTextRow }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={cn(
        "border-t",
        isDragging && "bg-indigo-50 shadow-lg",
        isTextRow && "bg-amber-50/50"
      )}
    >
      <td className="p-2 w-10">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded flex items-center justify-center"
          data-testid={`drag-handle-${id}`}
        >
          <GripVertical size={16} className="text-slate-400" />
        </div>
      </td>
      {children}
    </tr>
  );
};

// Account Combobox Component with search
const AccountCombobox = ({ accounts, value, onChange, index }) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const selectedAccount = accounts.find(a => a.number === value);
  
  const filteredAccounts = accounts.filter(acc => {
    const search = searchValue.toLowerCase();
    return (
      acc.number.toLowerCase().includes(search) ||
      acc.name.toLowerCase().includes(search)
    );
  });

  const handleSelect = (accountNumber) => {
    onChange(accountNumber);
    setOpen(false);
    setSearchValue("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 w-full justify-between text-left font-normal"
          data-testid={`line-account-${index}`}
        >
          {selectedAccount ? (
            <span className="truncate">
              <span className="font-mono">{selectedAccount.number}</span>
              <span className="text-slate-500 ml-1">- {selectedAccount.name}</span>
            </span>
          ) : (
            <span className="text-slate-400">Sök konto...</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Sök på kontonummer eller namn..."
            value={searchValue}
            onValueChange={setSearchValue}
            data-testid={`account-search-${index}`}
          />
          <CommandList>
            <CommandEmpty>Inga konton hittades för "{searchValue}"</CommandEmpty>
            <CommandGroup className="max-h-[250px] overflow-y-auto">
              {filteredAccounts.map((acc) => (
                <CommandItem
                  key={acc.id}
                  value={acc.number}
                  onSelect={() => handleSelect(acc.number)}
                  className="cursor-pointer"
                  data-testid={`account-option-${acc.number}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === acc.number ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-mono font-medium">{acc.number}</span>
                  <span className="ml-2 text-slate-600">{acc.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Simple Entry Row Component - Excel style (no Collapsible wrapper)
const EntryRow = ({ entry, formatCurrency, isEven, columnWidths }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const getStatus = () => {
    if (entry.document_ref) {
      return { icon: FileCheck, color: "text-green-600", bg: "bg-green-50", label: "Bokförd" };
    }
    return { icon: Clock, color: "text-amber-600", bg: "bg-amber-50", label: "Väntande" };
  };
  
  const status = getStatus();
  const StatusIcon = status.icon;

  return (
    <>
      <tr 
        className={cn(
          "h-9 border-b border-slate-200 hover:bg-indigo-50/50 transition-colors cursor-pointer",
          isEven ? "bg-white" : "bg-slate-50/30",
          isOpen && "bg-indigo-50"
        )}
        onClick={() => setIsOpen(!isOpen)}
        data-testid={`entry-row-${entry.entry_number}`}
      >
        <td className="px-3 py-2 border-r border-slate-200 font-mono text-sm text-indigo-600 font-medium" style={{ width: `${columnWidths?.verNr || 70}px` }}>
          {entry.entry_number}
        </td>
        <td className="px-3 py-2 border-r border-slate-200 font-mono text-sm text-slate-600" style={{ width: `${columnWidths?.datum || 90}px` }}>
          {formatShortDate(entry.date)}
        </td>
        <td className="px-3 py-2 border-r border-slate-200 text-sm text-slate-800 truncate" style={{ width: `${columnWidths?.beskrivning || 500}px` }}>
          {entry.description}
        </td>
        <td className="px-3 py-2 border-r border-slate-200 font-mono text-sm text-right font-medium" style={{ width: `${columnWidths?.belopp || 110}px` }}>
          {formatCurrency(entry.total_debit)}
        </td>
        <td className="px-3 py-2 text-center" style={{ width: `${columnWidths?.status || 80}px` }}>
          <div className="flex items-center justify-center gap-1">
            <span className={cn("inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium", status.bg, status.color)}>
              <StatusIcon size={12} />
            </span>
            <span className="p-0.5">
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </span>
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-50">
          <td colSpan={5} className="p-0 border-b border-slate-200">
            <div className="px-4 py-2 ml-4 border-l-2 border-indigo-300">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-slate-500 uppercase bg-slate-100">
                    <th className="text-left py-1 px-2 w-20 border border-slate-200">Konto</th>
                    <th className="text-left py-1 px-2 border border-slate-200">Kontonamn</th>
                    <th className="text-right py-1 px-2 w-24 border border-slate-200">Debet</th>
                    <th className="text-right py-1 px-2 w-24 border border-slate-200">Kredit</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.lines?.map((line, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} style={{ height: '28px' }}>
                      {line.type === "text" ? (
                        <td colSpan={4} className="py-1 px-2 border border-slate-200">
                          <span className="italic text-amber-600 flex items-center gap-1 text-xs">
                            <Type size={12} />
                            {line.text}
                          </span>
                        </td>
                      ) : (
                        <>
                          <td className="py-1 px-2 font-mono font-medium text-indigo-600 border border-slate-200">
                            {line.account_number || <span className="text-slate-400 italic">-</span>}
                          </td>
                          <td className="py-1 px-2 text-slate-600 border border-slate-200">
                            {line.account_name || <span className="text-slate-400 italic">(ej angivet)</span>}
                          </td>
                          <td className="py-1 px-2 text-right font-mono border border-slate-200">
                            {line.debit > 0 ? formatCurrency(line.debit) : ""}
                          </td>
                          <td className="py-1 px-2 text-right font-mono border border-slate-200">
                            {line.credit > 0 ? formatCurrency(line.credit) : ""}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-slate-100 font-medium text-xs" style={{ height: '28px' }}>
                    <td colSpan={2} className="py-1 px-2 text-right text-slate-600 border border-slate-200">Summa:</td>
                    <td className="py-1 px-2 text-right font-mono border border-slate-200">{formatCurrency(entry.total_debit)}</td>
                    <td className="py-1 px-2 text-right font-mono border border-slate-200">{formatCurrency(entry.total_credit)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Bookkeeping = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Sort & Filter state
  const [sortConfig, setSortConfig] = useState({ column: 'entry_number', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState({
    verNr: "",
    datum: "",
    beskrivning: "",
    belopp: "",
    status: ""
  });
  
  // Column widths state for resizable columns
  const [columnWidths, setColumnWidths] = useState({
    verNr: 70,
    datum: 90,
    beskrivning: 500,
    belopp: 110,
    status: 80
  });

  // Handle column resize
  const handleColumnResize = useCallback((columnKey, newWidth) => {
    setColumnWidths(prev => ({
      ...prev,
      [columnKey]: newWidth
    }));
  }, []);
  
  const [formData, setFormData] = useState({
    date: format(new Date(), "yyyy-MM-dd"),
    description: "",
    document_ref: "",
    lines: [
      { id: "line-1", type: "account", account_number: "", account_name: "", debit: 0, credit: 0, description: "" },
      { id: "line-2", type: "account", account_number: "", account_name: "", debit: 0, credit: 0, description: "" }
    ]
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [entriesRes, accountsRes] = await Promise.all([
        axios.get(`${API}/journal-entries`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/accounts`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEntries(entriesRes.data);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Kunde inte hämta data");
    } finally {
      setLoading(false);
    }
  };

  const generateLineId = () => `line-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const handleAddLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { 
        id: generateLineId(), 
        type: "account", 
        account_number: "", 
        account_name: "", 
        debit: 0, 
        credit: 0, 
        description: "" 
      }]
    });
  };

  const handleAddTextLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { 
        id: generateLineId(), 
        type: "text", 
        text: "" 
      }]
    });
  };

  const handleRemoveLine = (index) => {
    const accountLines = formData.lines.filter(l => l.type === "account");
    if (formData.lines[index].type === "account" && accountLines.length <= 2) {
      toast.error("Minst 2 konteringsrader krävs");
      return;
    }
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const handleLineChange = (index, field, value) => {
    const newLines = [...formData.lines];
    newLines[index][field] = value;
    
    if (field === "account_number") {
      const account = accounts.find(a => a.number === value);
      if (account) {
        newLines[index].account_name = account.name;
      }
    }
    
    setFormData({ ...formData, lines: newLines });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setFormData((prev) => {
        const oldIndex = prev.lines.findIndex(l => l.id === active.id);
        const newIndex = prev.lines.findIndex(l => l.id === over.id);
        
        return {
          ...prev,
          lines: arrayMove(prev.lines, oldIndex, newIndex)
        };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const accountLines = formData.lines.filter(l => l.type === "account");
    const totalDebit = accountLines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
    const totalCredit = accountLines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
    
    if (Math.abs(totalDebit - totalCredit) > 0.01) {
      toast.error(`Debet (${totalDebit.toFixed(2)}) och kredit (${totalCredit.toFixed(2)}) måste vara lika`);
      return;
    }

    const submissionData = {
      ...formData,
      lines: formData.lines.map(line => {
        if (line.type === "text") {
          return { type: "text", text: line.text };
        }
        return {
          type: "account",
          account_number: line.account_number,
          account_name: line.account_name,
          debit: line.debit,
          credit: line.credit,
          description: line.description
        };
      })
    };
    
    try {
      await axios.post(`${API}/journal-entries`, submissionData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Verifikation skapad");
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kunde inte skapa verifikation");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/journal-entries/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `bokforing_${format(new Date(), "yyyyMMdd")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export nedladdad");
    } catch (error) {
      toast.error("Kunde inte exportera");
    }
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), "yyyy-MM-dd"),
      description: "",
      document_ref: "",
      lines: [
        { id: "line-1", type: "account", account_number: "", account_name: "", debit: 0, credit: 0, description: "" },
        { id: "line-2", type: "account", account_number: "", account_name: "", debit: 0, credit: 0, description: "" }
      ]
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Sort handler
  const handleSort = useCallback((column) => {
    setSortConfig(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Filter handler
  const handleFilter = useCallback((column, value) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setColumnFilters({ verNr: "", datum: "", beskrivning: "", belopp: "", status: "" });
    setSearchTerm("");
  }, []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchTerm || Object.values(columnFilters).some(v => v);
  }, [searchTerm, columnFilters]);

  // Filter and sort entries
  const filteredAndSortedEntries = useMemo(() => {
    let result = [...entries];

    // Apply search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(entry =>
        entry.description.toLowerCase().includes(search) ||
        entry.entry_number.toString().includes(search)
      );
    }

    // Apply column filters
    if (columnFilters.verNr) {
      result = result.filter(e => e.entry_number.toString().includes(columnFilters.verNr));
    }
    if (columnFilters.datum) {
      result = result.filter(e => formatShortDate(e.date).includes(columnFilters.datum));
    }
    if (columnFilters.beskrivning) {
      const search = columnFilters.beskrivning.toLowerCase();
      result = result.filter(e => e.description.toLowerCase().includes(search));
    }
    if (columnFilters.belopp) {
      result = result.filter(e => e.total_debit.toString().includes(columnFilters.belopp));
    }
    if (columnFilters.status) {
      const search = columnFilters.status.toLowerCase();
      result = result.filter(e => {
        const hasDoc = e.document_ref ? "bokförd" : "väntande";
        return hasDoc.includes(search);
      });
    }

    // Apply sorting
    if (sortConfig.column) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        switch (sortConfig.column) {
          case 'verNr':
            aVal = a.entry_number;
            bVal = b.entry_number;
            break;
          case 'datum':
            aVal = new Date(a.date).getTime();
            bVal = new Date(b.date).getTime();
            break;
          case 'beskrivning':
            aVal = a.description.toLowerCase();
            bVal = b.description.toLowerCase();
            break;
          case 'belopp':
            aVal = a.total_debit;
            bVal = b.total_debit;
            break;
          case 'status':
            aVal = a.document_ref ? 1 : 0;
            bVal = b.document_ref ? 1 : 0;
            break;
          default:
            return 0;
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [entries, searchTerm, columnFilters, sortConfig]);

  const accountLines = formData.lines.filter(l => l.type === "account");
  const totalDebitForm = accountLines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0);
  const totalCreditForm = accountLines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0);
  const isBalanced = Math.abs(totalDebitForm - totalCreditForm) < 0.01;

  // Calculate totals
  const totalAmount = entries.reduce((sum, e) => sum + (e.total_debit || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="bookkeeping">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('bookkeeping.title')}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {filteredAndSortedEntries.length} av {entries.length} verifikationer • {formatCurrency(totalAmount)} totalt
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
            <Download size={14} className="mr-1.5" />
            Exportera
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-entry-btn">
                <Plus size={14} className="mr-1.5" />
                Ny verifikation
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Ny verifikation</DialogTitle>
                <DialogDescription>Skapa en ny bokföringspost. Dra raderna för att ändra ordning.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="date-picker">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={new Date(formData.date)}
                          onSelect={(date) => date && setFormData({ ...formData, date: format(date, "yyyy-MM-dd") })}
                          locale={sv}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Beskrivning</Label>
                    <Input
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Beskrivning av transaktionen"
                      required
                      data-testid="entry-description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="document_ref">Dokumentref</Label>
                    <Input
                      id="document_ref"
                      value={formData.document_ref}
                      onChange={(e) => setFormData({ ...formData, document_ref: e.target.value })}
                      placeholder="Fakturanummer etc."
                      data-testid="entry-docref"
                    />
                  </div>
                </div>

                {/* Journal Lines */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Konteringsrader</Label>
                    <div className="flex gap-2">
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={handleAddTextLine} 
                        data-testid="add-text-line-btn"
                        className="text-amber-600 border-amber-300 hover:bg-amber-50"
                      >
                        <Type size={14} className="mr-1" />
                        Lägg till textrad
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={handleAddLine} data-testid="add-line-btn">
                        <Plus size={14} className="mr-1" />
                        Lägg till rad
                      </Button>
                    </div>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="w-10"></th>
                          <th className="text-left text-xs font-medium text-slate-500 uppercase p-3 w-48">Konto</th>
                          <th className="text-left text-xs font-medium text-slate-500 uppercase p-3">Kontonamn</th>
                          <th className="text-right text-xs font-medium text-slate-500 uppercase p-3 w-32">Debet</th>
                          <th className="text-right text-xs font-medium text-slate-500 uppercase p-3 w-32">Kredit</th>
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                      >
                        <tbody>
                          <SortableContext
                            items={formData.lines.map(l => l.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {formData.lines.map((line, index) => (
                              <SortableRow key={line.id} id={line.id} isTextRow={line.type === "text"}>
                                {line.type === "text" ? (
                                  <>
                                    <td colSpan="4" className="p-2">
                                      <div className="flex items-center gap-2">
                                        <Type size={14} className="text-amber-500 flex-shrink-0" />
                                        <Input
                                          value={line.text || ""}
                                          onChange={(e) => handleLineChange(index, "text", e.target.value)}
                                          className="h-9 flex-1"
                                          placeholder="Ange valfri text..."
                                          data-testid={`text-line-${index}`}
                                        />
                                      </div>
                                    </td>
                                    <td className="p-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveLine(index)}
                                        className="text-red-500 hover:text-red-600"
                                        data-testid={`remove-line-${index}`}
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td className="p-2">
                                      <AccountCombobox
                                        accounts={accounts}
                                        value={line.account_number}
                                        onChange={(value) => handleLineChange(index, "account_number", value)}
                                        index={index}
                                      />
                                    </td>
                                    <td className="p-2">
                                      <Input
                                        value={line.account_name}
                                        onChange={(e) => handleLineChange(index, "account_name", e.target.value)}
                                        className="h-9"
                                        placeholder="Kontonamn"
                                        data-testid={`line-name-${index}`}
                                      />
                                    </td>
                                    <td className="p-2">
                                      <Input
                                        type="number"
                                        value={line.debit || ""}
                                        onChange={(e) => handleLineChange(index, "debit", parseFloat(e.target.value) || 0)}
                                        className="h-9 text-right font-mono"
                                        placeholder="0.00"
                                        step="0.01"
                                        data-testid={`line-debit-${index}`}
                                      />
                                    </td>
                                    <td className="p-2">
                                      <Input
                                        type="number"
                                        value={line.credit || ""}
                                        onChange={(e) => handleLineChange(index, "credit", parseFloat(e.target.value) || 0)}
                                        className="h-9 text-right font-mono"
                                        placeholder="0.00"
                                        step="0.01"
                                        data-testid={`line-credit-${index}`}
                                      />
                                    </td>
                                    <td className="p-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveLine(index)}
                                        className="text-red-500 hover:text-red-600"
                                        data-testid={`remove-line-${index}`}
                                      >
                                        <Trash2 size={14} />
                                      </Button>
                                    </td>
                                  </>
                                )}
                              </SortableRow>
                            ))}
                          </SortableContext>
                          <tr className="border-t bg-slate-50 font-medium">
                            <td></td>
                            <td colSpan="2" className="p-3 text-right">Summa:</td>
                            <td className="p-3 text-right font-mono">{formatCurrency(totalDebitForm)}</td>
                            <td className="p-3 text-right font-mono">{formatCurrency(totalCreditForm)}</td>
                            <td></td>
                          </tr>
                        </tbody>
                      </DndContext>
                    </table>
                  </div>
                  {!isBalanced && (
                    <p className="text-sm text-red-500">
                      Differens: {formatCurrency(Math.abs(totalDebitForm - totalCreditForm))}
                    </p>
                  )}
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Avbryt
                  </Button>
                  <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={!isBalanced} data-testid="save-entry-btn">
                    Spara verifikation
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input
            placeholder="Sök verifikationer..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9"
            data-testid="search-entries"
          />
        </div>
        {hasActiveFilters && (
          <Button variant="outline" size="sm" onClick={clearAllFilters} className="h-9 text-slate-600">
            <X size={14} className="mr-1" />
            Rensa filter
          </Button>
        )}
      </div>

      {/* Excel-style Table with Resizable Columns */}
      <Card className="card-base overflow-hidden border border-slate-300" data-testid="entries-table">
        <CardContent className="p-0">
          {filteredAndSortedEntries.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ tableLayout: 'fixed', minWidth: `${columnWidths.verNr + columnWidths.datum + columnWidths.beskrivning + columnWidths.belopp + columnWidths.status}px` }}>
                <colgroup>
                  <col style={{ width: `${columnWidths.verNr}px` }} />
                  <col style={{ width: `${columnWidths.datum}px` }} />
                  <col style={{ width: `${columnWidths.beskrivning}px` }} />
                  <col style={{ width: `${columnWidths.belopp}px` }} />
                  <col style={{ width: `${columnWidths.status}px` }} />
                </colgroup>
                <thead className="bg-slate-100 border-b border-slate-300">
                  <tr style={{ height: '36px' }}>
                    <ResizableColumnHeader
                      columnKey="verNr"
                      width={columnWidths.verNr}
                      onResize={handleColumnResize}
                      minWidth={50}
                    >
                      <button onClick={() => handleSort('verNr')} className="flex items-center gap-1 hover:text-indigo-600">
                        {sortConfig.column === 'verNr' ? (sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Ver.nr
                      </button>
                    </ResizableColumnHeader>
                    <ResizableColumnHeader
                      columnKey="datum"
                      width={columnWidths.datum}
                      onResize={handleColumnResize}
                      minWidth={70}
                    >
                      <button onClick={() => handleSort('datum')} className="flex items-center gap-1 hover:text-indigo-600">
                        {sortConfig.column === 'datum' ? (sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Datum
                      </button>
                    </ResizableColumnHeader>
                    <ResizableColumnHeader
                      columnKey="beskrivning"
                      width={columnWidths.beskrivning}
                      onResize={handleColumnResize}
                      minWidth={100}
                    >
                      <button onClick={() => handleSort('beskrivning')} className="flex items-center gap-1 hover:text-indigo-600">
                        {sortConfig.column === 'beskrivning' ? (sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Beskrivning
                      </button>
                    </ResizableColumnHeader>
                    <ResizableColumnHeader
                      columnKey="belopp"
                      width={columnWidths.belopp}
                      onResize={handleColumnResize}
                      minWidth={80}
                      align="right"
                    >
                      <button onClick={() => handleSort('belopp')} className="flex items-center gap-1 justify-end hover:text-indigo-600 w-full">
                        {sortConfig.column === 'belopp' ? (sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Belopp
                      </button>
                    </ResizableColumnHeader>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-slate-600 uppercase tracking-wide" style={{ width: `${columnWidths.status}px` }}>
                      <button onClick={() => handleSort('status')} className="flex items-center gap-1 justify-center hover:text-indigo-600 w-full">
                        {sortConfig.column === 'status' ? (sortConfig.direction === 'asc' ? <ArrowUp size={12}/> : <ArrowDown size={12}/>) : <ArrowUpDown size={12} className="text-slate-400"/>}
                        Status
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedEntries.map((entry, index) => (
                    <EntryRow 
                      key={entry.id} 
                      entry={entry} 
                      formatCurrency={formatCurrency}
                      isEven={index % 2 === 0}
                      columnWidths={columnWidths}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p>Inga verifikationer matchar filtret</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearAllFilters} className="mt-2 text-indigo-600">
                  Rensa alla filter
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Bookkeeping;
